import { useRef, useState, useLayoutEffect, useEffect } from 'react';
import '../styles/ServiceLanding.css';
import { getEmailJsTemplateId, sendEmailJsForm } from '../utils/emailjsClient';
import { forceScrollWindowToTop } from '../utils/scrollWindow';
import ReCAPTCHA from 'react-google-recaptcha';
import { getRecaptchaSiteKey } from '../utils/recaptcha';
import { useRecaptchaGate } from '../hooks/useRecaptchaGate';
import imgRekrytering from '../images/services/rekrytering.jpg';
import imgBemanning from '../images/services/bemanning.jpg';
import imgInterim from '../images/services/interimlosning.jpg';

const HERO_BY_VARIANT = {
  rex: imgRekrytering,
  bem: imgBemanning,
  interim: imgInterim
};

const copy = {
  rex: {
    swe: {
      kicker: 'Rekrytering',
      title: 'Rätt person för långsiktig tillväxt',
      lead:
        'Vi hjälper er att tillsätta roller där kultur, kompetens och affär ska sitta ihop, från specialist till ledande positioner.',
      paragraphs: [
        'Genom tydlig kravbild, strukturerad urvalsprocess och nära dialog med er organisation hittar vi kandidater som både levererar i rollen och förstärker teamet.',
        'Oavsett om ni söker ingenjörer, ekonomer, IT-profiler eller andra specialister utgår vi alltid från ert behov och er tidsplan.'
      ],
      ctaEyebrow: 'Nästa steg',
      ctaHeading: 'Vill ni prata vidare om er rekrytering?',
      ctaText: 'Lämna era uppgifter så återkommer vi med ett konkret förslag på hur vi kan stötta er.',
      backLabel: 'Tillbaka till tjänster',
      contextTitle: 'Tjänstesida: Rekrytering',
      form: {
        name: 'Namn',
        company: 'Företagsnamn',
        phone: 'Telefonnummer',
        email: 'E-post',
        button: 'Skicka'
      }
    },
    eng: {
      kicker: 'Recruitment',
      title: 'The right hire for lasting growth',
      lead:
        'We help you fill roles where culture, competence, and business impact must align, from specialists to leadership positions.',
      paragraphs: [
        'With a clear requirements profile, structured selection, and close dialogue with your organisation, we identify candidates who deliver in the role and strengthen the team.',
        'Whether you need engineers, finance professionals, IT specialists, or other profiles, we always start from your needs and timeline.'
      ],
      ctaEyebrow: 'Next step',
      ctaHeading: 'Want to talk through your recruitment needs?',
      ctaText: 'Share your details and we will respond with a concrete proposal for how we can support you.',
      backLabel: 'Back to services',
      contextTitle: 'Service page: Recruitment',
      form: {
        name: 'Name',
        company: 'Company name',
        phone: 'Phone number',
        email: 'Email',
        button: 'Send'
      }
    }
  },
  bem: {
    swe: {
      kicker: 'Bemanning',
      title: 'Förstärkning när behovet uppstår',
      lead:
        'När ni behöver rätt kompetens under en period hjälper vi er att hitta specialister som snabbt kommer in i arbetet.',
      paragraphs: [
        'Vi arbetar med bemanning inom teknik, ingenjörskap, IT och ekonomi, med fokus på kvalitet, tydliga förväntningar och smidig start.',
        'Ni får en partner som förstår både leverans och tempo, oavsett om uppdraget är kortare eller mer långsiktigt.'
      ],
      ctaEyebrow: 'Kontakt',
      ctaHeading: 'Behöver ni diskutera bemanningsbehov?',
      ctaText: 'Berätta kort vem ni söker så återkommer vi med nästa steg.',
      backLabel: 'Tillbaka till tjänster',
      contextTitle: 'Tjänstesida: Bemanning',
      form: {
        name: 'Namn',
        company: 'Företagsnamn',
        phone: 'Telefonnummer',
        email: 'E-post',
        button: 'Skicka'
      }
    },
    eng: {
      kicker: 'Staffing',
      title: 'Capacity when you need it most',
      lead:
        'When you need the right competence for a defined period, we help you find specialists who ramp up quickly.',
      paragraphs: [
        'We provide staffing across technology, engineering, IT, and finance, with emphasis on quality, clear expectations, and a smooth start.',
        'You get a partner who understands both delivery and pace, whether the assignment is shorter or longer term.'
      ],
      ctaEyebrow: 'Contact',
      ctaHeading: 'Want to discuss a staffing need?',
      ctaText: 'Share a short note on who you are looking for and we will follow up with clear next steps.',
      backLabel: 'Back to services',
      contextTitle: 'Service page: Staffing',
      form: {
        name: 'Name',
        company: 'Company name',
        phone: 'Phone number',
        email: 'Email',
        button: 'Send'
      }
    }
  },
  interim: {
    swe: {
      kicker: 'Interimlösningar',
      title: 'Erfaren ledning i förändring',
      lead:
        'När läget kräver snabb och tydlig förstärkning levererar vi interimprofiler som kliver in och skapar struktur direkt.',
      paragraphs: [
        'Interim passar vid vakanser, transformationer eller när ni behöver någon som tar över och driver arbetet framåt utan dröjsmål.',
        'Vi matchar er med profiler som har rätt erfarenhet för uppdragets komplexitet och er kultur.'
      ],
      ctaEyebrow: 'Uppdrag',
      ctaHeading: 'Söker ni en interimlösning?',
      ctaText: 'Hör av er så återkommer vi med hur vi kan stötta er i nästa steg.',
      backLabel: 'Tillbaka till tjänster',
      contextTitle: 'Tjänstesida: Interim',
      form: {
        name: 'Namn',
        company: 'Företagsnamn',
        phone: 'Telefonnummer',
        email: 'E-post',
        button: 'Skicka'
      }
    },
    eng: {
      kicker: 'Interim solutions',
      title: 'Experienced leadership in transition',
      lead:
        'When you need fast, decisive reinforcement, we deliver interim professionals who create structure from day one.',
      paragraphs: [
        'Interim fits vacancies, transformations, or when you need someone to take ownership and move work forward without delay.',
        'We match you with profiles who have the right experience for the assignment and your culture.'
      ],
      ctaEyebrow: 'Assignment',
      ctaHeading: 'Looking for an interim solution?',
      ctaText: 'Reach out and we will respond with how we can support your next step.',
      backLabel: 'Back to services',
      contextTitle: 'Service page: Interim',
      form: {
        name: 'Name',
        company: 'Company name',
        phone: 'Phone number',
        email: 'Email',
        button: 'Send'
      }
    }
  }
};

const SOURCE_BY_VARIANT = {
  rex: 'service-rex',
  bem: 'service-bem',
  interim: 'service-interim'
};

function ServiceLanding({ lang = 'swe', variant = 'rex', navigateTo }) {
  const text = copy[variant][lang] || copy[variant].swe;
  const heroImage = HERO_BY_VARIANT[variant];
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const formRef = useRef(null);
  const captchaRef = useRef(null);
  const [recaptchaAllowed, syncRecaptchaGate] = useRecaptchaGate(formRef, captchaRef, setCaptchaToken);

  useLayoutEffect(() => {
    forceScrollWindowToTop();
  }, [variant]);

  useEffect(() => {
    forceScrollWindowToTop();
    const onPageShow = () => {
      forceScrollWindowToTop();
    };
    window.addEventListener('pageshow', onPageShow);
    const timers = [50, 200, 500].map((ms) =>
      window.setTimeout(() => {
        forceScrollWindowToTop();
      }, ms)
    );
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [variant]);

  const captchaExpiredMessage = lang === 'swe'
    ? 'Verifieringen gick ut. Bekräfta att du inte är en robot igen.'
    : 'Verification expired. Please confirm you are not a robot again.';
  const captchaErrorMessage = lang === 'swe'
    ? 'reCAPTCHA kunde inte laddas klart. Ladda om sidan och försök igen.'
    : 'reCAPTCHA could not finish loading. Refresh the page and try again.';

  const handlePhoneChange = (event) => {
    const raw = event.target.value;
    setPhone(raw.replace(/[^0-9+\s-]/g, ''));
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken('');
    if (status === 'sending') {
      setStatus('idle');
    }
    setError(captchaExpiredMessage);
  };

  const handleCaptchaErrored = () => {
    setCaptchaToken('');
    if (status === 'sending') {
      setStatus('idle');
    }
    setError(captchaErrorMessage);
    captchaRef.current?.reset?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const formEl = formRef.current;
    if (!formEl) {
      return;
    }
    if (!formEl.checkValidity()) {
      formEl.reportValidity();
      return;
    }

    setStatus('sending');

    try {
      const siteKey = getRecaptchaSiteKey();
      const token = captchaRef.current?.getValue?.() || captchaToken;
      if (siteKey && !token) {
        setStatus('idle');
        setError(lang === 'swe' ? 'Bekräfta att du inte är en robot.' : 'Please confirm you are not a robot.');
        return;
      }

      const timeEl = formEl.querySelector('input[name="time"]');
      if (timeEl) {
        timeEl.value = new Date().toLocaleString(lang === 'swe' ? 'sv-SE' : 'en-GB', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }

      await sendEmailJsForm(formEl, getEmailJsTemplateId('cta'));
      setStatus('sent');
      formEl.reset();
      setPhone('');
      syncRecaptchaGate();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('EmailJS send failed (service CTA)', err);
      setStatus('idle');
      setError(lang === 'swe' ? 'Kunde inte skicka just nu. Försök igen.' : 'Could not send right now. Please try again.');
    }
  };

  const recaptchaHint = lang === 'swe'
    ? 'Fyll i alla obligatoriska fält ovan för att kunna använda reCAPTCHA.'
    : 'Complete all required fields above to use reCAPTCHA.';

  const successMessage = lang === 'swe' ? 'Tack! Vi hör av oss snart.' : 'Thank you! We will get back to you shortly.';

  return (
    <div className="service-landing" id="service-top">
      <section
        className="service-hero"
        style={{ backgroundImage: `url(${heroImage})` }}
        aria-label={text.title}
      >
        <div className="service-hero-overlay" aria-hidden="true" />
        <div className="service-hero-inner">
          <button type="button" className="service-back" onClick={() => navigateTo('services')}>
            {text.backLabel}
          </button>
          <p className="service-hero-kicker">{text.kicker}</p>
          <h1 className="service-hero-title">{text.title}</h1>
          <p className="service-hero-lead">{text.lead}</p>
        </div>
      </section>

      <section className="service-body" aria-label={lang === 'swe' ? 'Beskrivning' : 'Description'}>
        <div className="service-body-inner">
          {text.paragraphs.map((p, i) => (
            <p key={`${variant}-${i}`}>{p}</p>
          ))}
        </div>
      </section>

      <div className="service-cta-wrap" id="service-cta">
        <div className="service-cta-strip">
          <div className="service-cta-copy">
            <p className="service-cta-eyebrow">{text.ctaEyebrow}</p>
            <h2 className="service-cta-heading">{text.ctaHeading}</h2>
            <p className="service-cta-text">{text.ctaText}</p>
          </div>

          <form
            ref={formRef}
            className="service-cta-form"
            onSubmit={handleSubmit}
            onInput={syncRecaptchaGate}
            onChange={syncRecaptchaGate}
          >
            <input type="hidden" name="source" value={SOURCE_BY_VARIANT[variant]} />
            <input type="hidden" name="time" value="" />
            <input type="hidden" name="context_title" value={text.contextTitle} />
            <div className="service-cta-row">
              <label>
                <span>{text.form.name}</span>
                <input type="text" name="name" placeholder="Anna Andersson" required />
              </label>
              <label>
                <span>{text.form.company}</span>
                <input type="text" name="company" placeholder="Acme AB" required />
              </label>
            </div>
            <div className="service-cta-row">
              <label>
                <span>{text.form.phone}</span>
                <input
                  type="tel"
                  name="phone"
                  placeholder="070-123 45 67"
                  required
                  title={lang === 'swe' ? 'Ange bara siffror, mellanslag eller bindestreck' : 'Use only numbers, spaces, or dashes'}
                  value={phone}
                  onChange={handlePhoneChange}
                />
              </label>
              <label>
                <span>{text.form.email}</span>
                <input type="email" name="email" placeholder="anna@acme.se" required />
              </label>
            </div>

            {getRecaptchaSiteKey() &&
              (recaptchaAllowed ? (
                <div className="recaptcha-wrap">
                  <ReCAPTCHA
                    ref={captchaRef}
                    sitekey={getRecaptchaSiteKey()}
                    onChange={(token) => setCaptchaToken(token || '')}
                    onExpired={handleCaptchaExpired}
                    onErrored={handleCaptchaErrored}
                    hl={lang === 'swe' ? 'sv' : 'en'}
                  />
                </div>
              ) : (
                <p className="recaptcha-hint" role="status">
                  {recaptchaHint}
                </p>
              ))}

            <button type="submit" className="service-cta-submit" disabled={status === 'sending'}>
              {status === 'sending' ? (lang === 'swe' ? 'Skickar…' : 'Sending…') : text.form.button}
            </button>
            {error && <p className="service-cta-status">{error}</p>}
            {status === 'sent' && !error && <p className="service-cta-status">{successMessage}</p>}
          </form>
        </div>
      </div>
    </div>
  );
}

export default ServiceLanding;
